/**
 * play.thegamer.network entry point.
 *
 * URL shape: `/<handle>` → live player view for that streamer.
 * `/` → landing page (search by handle).
 *
 * On first visit we prompt the player for `platform` + `tag`; subsequent
 * visits read those from localStorage. The WS endpoint is
 * `wss://api.../play/<handle>/<platform>:<tag>`.
 */

import { applyFrame, initialState, type PlayerSpaState } from './state.js';

const PUBLIC_API_HOST =
  (import.meta.env.VITE_PUBLIC_API_HOST as string | undefined) ?? 'https://api.thegamer.network';

const LS_PLATFORM = 'sg:play:platform';
const LS_TAG = 'sg:play:tag';

const appEl = document.getElementById('app')!;
let state: PlayerSpaState = initialState();
let ws: WebSocket | null = null;

bootstrap();

function bootstrap(): void {
  const path = window.location.pathname.replace(/^\//, '').replace(/\/$/, '');
  if (!path) {
    renderLanding();
    return;
  }
  state.handle = path;
  const cachedPlatform = readStored(LS_PLATFORM);
  const cachedTag = readStored(LS_TAG);
  if (cachedPlatform && cachedTag) {
    state.platform = cachedPlatform as PlayerSpaState['platform'];
    state.tag = cachedTag;
    connect();
  } else {
    renderJoinForm();
  }
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore — private browsing
  }
}

function renderLanding(): void {
  appEl.innerHTML = `
    <h1>Play on The Gamer Network</h1>
    <p class="muted">Enter the streamer's handle to join their game:</p>
    <form id="landing-form">
      <input id="handle-input" placeholder="streamer-handle" autocapitalize="off" autocorrect="off" />
      <button type="submit">Join</button>
    </form>
  `;
  const form = appEl.querySelector('#landing-form') as HTMLFormElement;
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const input = appEl.querySelector('#handle-input') as HTMLInputElement;
    const handle = input.value.trim().toLowerCase();
    if (!handle) return;
    window.location.pathname = `/${handle}`;
  });
}

function renderJoinForm(): void {
  appEl.innerHTML = `
    <h1>Join @${escapeHtml(state.handle ?? '')}</h1>
    <p class="muted">Pick your platform and type your tag. We use this to merge your score with your chat identity.</p>
    <form id="join-form" class="card">
      <label>Platform</label>
      <select id="platform-select">
        <option value="twitch">Twitch</option>
        <option value="youtube">YouTube</option>
        <option value="kick">Kick</option>
        <option value="web">Web only</option>
      </select>
      <label style="margin-top: 0.75rem;">Your tag / handle</label>
      <input id="tag-input" placeholder="dazaster83" autocapitalize="off" autocorrect="off" />
      <button type="submit" style="margin-top: 0.75rem;">Join</button>
    </form>
  `;
  const form = appEl.querySelector('#join-form') as HTMLFormElement;
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const platform = (appEl.querySelector('#platform-select') as HTMLSelectElement)
      .value as PlayerSpaState['platform'];
    const tag = (appEl.querySelector('#tag-input') as HTMLInputElement).value.trim();
    if (!tag || !platform) return;
    if (!/^[a-zA-Z0-9_.-]+$/.test(tag)) {
      // simple client-side validator — server will re-validate
      state.message = 'Tag may only contain letters, numbers, _ . -';
      renderJoinForm();
      return;
    }
    state.platform = platform;
    state.tag = tag;
    writeStored(LS_PLATFORM, platform);
    writeStored(LS_TAG, tag);
    connect();
  });
}

function connect(): void {
  if (!state.handle || !state.platform || !state.tag) return;
  state.status = 'connecting';
  state.closeReason = null;
  renderPlayer();

  const wsOrigin = PUBLIC_API_HOST.replace(/^http/, 'ws');
  const playerId = `${state.platform}:${state.tag}`;
  ws = new WebSocket(
    `${wsOrigin}/play/${encodeURIComponent(state.handle)}/${encodeURIComponent(playerId)}`,
  );

  ws.addEventListener('open', () => {
    state.status = 'connected';
    renderPlayer();
  });
  ws.addEventListener('close', (ev) => {
    state.status = 'closed';
    state.closeReason = `${ev.code}${ev.reason ? `: ${ev.reason}` : ''}`;
    renderPlayer();
  });
  ws.addEventListener('error', () => {
    state.status = 'error';
    renderPlayer();
  });
  ws.addEventListener('message', (ev) => {
    try {
      const frame = JSON.parse(String(ev.data)) as { type: string } & Record<string, unknown>;
      applyFrame(state, frame);
      renderPlayer();
    } catch {
      // ignore
    }
  });
}

function sendGuess(word: string): void {
  if (!ws || ws.readyState !== ws.OPEN) return;
  try {
    ws.send(JSON.stringify({ type: 'guess', word }));
  } catch {
    // ignore
  }
}

function renderPlayer(): void {
  appEl.innerHTML = `
    <div class="row" style="justify-content: space-between; align-items: center;">
      <h1>@${escapeHtml(state.handle ?? '')}</h1>
      <button id="leave-btn" style="background: transparent; color: var(--c-muted); border-color: transparent;">Change tag</button>
    </div>
    <div class="muted" id="connection-line">
      ${connectionLabel()}
    </div>
    ${state.message ? `<div class="status">${escapeHtml(state.message)}</div>` : ''}
    ${renderGrid()}
    ${renderKeyboard()}
  `;
  appEl.querySelector('#leave-btn')!.addEventListener('click', () => {
    try {
      localStorage.removeItem(LS_PLATFORM);
      localStorage.removeItem(LS_TAG);
    } catch { /* ignore */ }
    state = initialState();
    state.handle = window.location.pathname.replace(/^\//, '').replace(/\/$/, '');
    ws?.close();
    renderJoinForm();
  });
  for (const key of appEl.querySelectorAll<HTMLElement>('[data-key]')) {
    key.addEventListener('click', () => handleKey(key.dataset.key!));
  }
}

function connectionLabel(): string {
  switch (state.status) {
    case 'connecting':
      return 'Connecting…';
    case 'connected':
      return state.roundActive ? 'Round in progress' : 'Waiting for next round';
    case 'closed':
      return `Disconnected ${state.closeReason ?? ''}`;
    case 'error':
      return 'Connection error';
    default:
      return '';
  }
}

function renderGrid(): string {
  const rows: string[] = [];
  const totalRows = state.maxTries;
  for (let r = 0; r < totalRows; r++) {
    const scored = state.rows[r];
    const isPendingRow = r === state.rows.length && state.roundActive;
    const cells: string[] = [];
    for (let c = 0; c < state.wordLength; c++) {
      if (scored) {
        const cell = scored[c];
        cells.push(
          `<div class="cell ${cell?.state ?? ''}">${escapeHtml((cell?.letter ?? '').toUpperCase())}</div>`,
        );
      } else if (isPendingRow) {
        const ch = state.pending[c] ?? '';
        cells.push(`<div class="cell pending">${escapeHtml(ch.toUpperCase())}</div>`);
      } else {
        cells.push(`<div class="cell empty"></div>`);
      }
    }
    rows.push(
      `<div class="row" style="grid-template-columns: repeat(${state.wordLength}, 1fr);">${cells.join('')}</div>`,
    );
  }
  return `<div class="card grid">${rows.join('')}</div>`;
}

function renderKeyboard(): string {
  // Use a simple QWERTY layout. Letter coloring is derived from prior rows.
  const order: Record<'correct' | 'present' | 'absent', number> = {
    absent: 0,
    present: 1,
    correct: 2,
  };
  const keyStates: Record<string, 'correct' | 'present' | 'absent'> = {};
  for (const row of state.rows) {
    for (const cell of row) {
      const prev = keyStates[cell.letter];
      if (prev === undefined || order[cell.state] > order[prev]) {
        keyStates[cell.letter] = cell.state;
      }
    }
  }
  const rows = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];
  const kbHtml = rows
    .map((rowLetters, idx) => {
      const keys = rowLetters
        .split('')
        .map((l) => `<div class="key ${keyStates[l] ?? ''}" data-key="${l}">${l.toUpperCase()}</div>`)
        .join('');
      if (idx === 2) {
        return `<div class="kbrow"><div class="key wide" data-key="ENTER">Enter</div>${keys}<div class="key wide" data-key="BACK">⌫</div></div>`;
      }
      return `<div class="kbrow">${keys}</div>`;
    })
    .join('');
  return `<div class="keyboard">${kbHtml}</div>`;
}

function handleKey(k: string): void {
  if (!state.roundActive) return;
  if (k === 'ENTER') {
    if (state.pending.length !== state.wordLength) {
      state.message = 'Type a full word';
      renderPlayer();
      return;
    }
    sendGuess(state.pending);
    return;
  }
  if (k === 'BACK') {
    state.pending = state.pending.slice(0, -1);
    renderPlayer();
    return;
  }
  if (state.pending.length >= state.wordLength) return;
  if (!/^[a-z]$/.test(k)) return;
  state.pending += k;
  renderPlayer();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

// Hardware keyboard support — many players will be on tablets / laptops.
window.addEventListener('keydown', (ev) => {
  if (!state.roundActive) return;
  if (ev.key === 'Enter') { handleKey('ENTER'); return; }
  if (ev.key === 'Backspace') { handleKey('BACK'); return; }
  if (/^[a-zA-Z]$/.test(ev.key)) handleKey(ev.key.toLowerCase());
});
